import React from 'react'

import ModalRoot from '@ui/components/ModalRoot'
import { formatCurrency } from '@utils/currency'
import { useAppContext } from '@hooks/use-app-context'
import barca from '@assets/Barca.png'
import formatPercentage from '@utils/format'
import { useAtomValue } from 'jotai'
import {
	selectedSondInstallmentAtom,
	mainChoiceAtom,
	identinetChoiceAtom,
} from '@store/atoms'

type ModalProps = {
	isOpen: boolean
	onCloseModal: () => void
}

export const OffertaPreview = ({ isOpen, onCloseModal }: ModalProps) => {
	const { state } = useAppContext()

	// Live atoms — updated in real-time by InsuranceSelection and IdentinetForm
	// so the preview reflects choices before Prosegui is clicked.
	const mainChoice = useAtomValue(mainChoiceAtom)
	const identinetChoice = useAtomValue(identinetChoiceAtom)
	const liveSondInstallment = useAtomValue(selectedSondInstallmentAtom)

	// Fall back to committed context state when no in-progress live choice exists.
	// Insurance is only considered live-active when a rate card has also been chosen
	// (liveSondInstallment is populated by rate selection, not just the Sì radio).
	const liveInsurance =
		mainChoice === 'si' && liveSondInstallment !== null
			? true
			: mainChoice === 'no'
				? false
				: state.data.One.Insurance
	const liveIdentinet =
		identinetChoice !== null
			? identinetChoice === 'yes'
			: state.data.Seven.IdentinetActive

	const liveHasService = liveInsurance || liveIdentinet
	// Prefer the live atom value; fall back to the committed installment from context.
	const bestSondInst =
		liveSondInstallment ?? state.data.Seven.SelectedInstallmentSond
	const inst = liveHasService
		? bestSondInst
		: state.data.One.SelectedInstallment

	const onExit = () => {
		onCloseModal()
	}

	return (
		<div>
			<ModalRoot
				isOpen={isOpen}
				onCloseModal={onCloseModal}
				title={'La tua richiesta'}
				onCallBack={onExit}
				labelOk={''}
				labelClose={''}
				disableButton={true}
				disableButtonAdd={true}
				reverseButton={false}
				
				height='h-[500px] md:h-[360px] xl:h-[400px]'
			>
				<div className='w-full bg-white rounded-md'>
					{/* BLOCCO ALTO */}
					<div className='flex justify-between items-start gap-2 md:gap-6 mb-3 xl:mb-6'>
						{/* PARTE SINISTRA */}
						<div className='flex-1 min-w-0'>
							{/* riga 1 */}
							<div className='flex items-baseline gap-3 mb-3 xl:mb-8'>
								<p className='text-2xl md:text-[28px] leading-none font-semibold'>
									{formatCurrency(inst?.Amount)}
								</p>
								<span className='text-[13px] uppercase whitespace-nowrap'>
									Importo della rata
								</span>
							</div>

							{/* riga 2 */}
							<div className='flex justify-start items-end gap-0'>
								<div className='grid md:grid-cols-2 gap-6 md:gap-32'>
									<div>
										<div className='text-[13px] uppercase mb-2 whitespace-nowrap'>
											Importo richiesto
										</div>
										<div className='font-bold'>
											{formatCurrency(Number(state.data.One.Amount))}
										</div>
									</div>
									<div className='grid grid-cols-2 items-end'>
										<div>
											<div className='text-[12px] uppercase mb-2'>
												Numero rate
											</div>
											<div className='font-bold'>{inst?.InstallmentsCount}</div>
										</div>
											<div className='md:hidden flex justify-end'>
											<img
												src={barca}
												alt='img'
												className='w-44 h-auto object-contain'
											/>
										</div>
									</div>
								</div>
							</div>
						</div>

						{/* PARTE DESTRA */}
						<div className='hidden md:block shrink-0 self-start'>
							<img
								src={barca}
								alt='img'
								className='md:w-[350px] h-auto object-contain'
							/>
						</div>
					</div>

					{/* BLOCCO BASSO */}
					<div className='border-t-[0.5px] border-(--strokes-grey) pt-6'>
						
						<div className='grid grid-cols-1 md:grid-cols-4 md:gap-[219px] landscape:gap-36'>
							<div className='flex flex-row justify-between md:flex-col'>
								<div className='text-[12px] uppercase mb-2'>Progetto</div>
								<div className='font-bold whitespace-nowrap'>
									{state.data.One.DescrMotivazione}
								</div>
							</div>

							<div className='flex flex-row justify-between md:flex-col'>
								<div className='text-[12px] uppercase mb-2 whitespace-nowrap'>
									Polizza assicurativa
								</div>
								<div className='font-bold'>{liveInsurance ? 'Si' : 'No'}</div>
							</div>

							<div className='flex flex-row justify-between md:flex-col'>
								<div className='text-[12px] uppercase mb-2'>TAEG</div>
								<div className='font-bold'>{formatPercentage(inst?.Taeg)}</div>
							</div>

							<div className='flex flex-row justify-between md:flex-col'>
								<div className='text-[12px] uppercase mb-2'>TAN</div>
								<div className='font-bold'>{formatPercentage(inst?.Tan)}</div>
							</div>
						</div>
					</div>
				</div>
			</ModalRoot>
		</div>
	)
}
